/**
 * useEvents Hook
 * Manages event search, pagination, loading, and error states
 */

import {useCallback, useRef, useState} from 'react';

import {EventData, EventSearchResponse, EventSearchParams, toggleEventStatus} from './services/eventsApi';
import type {ToggleAction} from './buildStatusMessage';
import {ROOT} from '../../middleware/api';

// Simple async wrapper for the Redux action
const searchEventsAsync = async (params: EventSearchParams): Promise<EventSearchResponse> => {
    const queryParams = new URLSearchParams();
    
    // Add optional parameters only if they have values
    if (params.searchText) queryParams.append('searchText', params.searchText);
    if (params.searchType) queryParams.append('searchType', params.searchType);
    if (params.searchField) queryParams.append('searchField', params.searchField);
    if (params.eventType) queryParams.append('eventType', params.eventType);
    if (params.eventGroup) queryParams.append('eventGroup', params.eventGroup);
    if (params.coverage) queryParams.append('coverage', params.coverage);
    if (params.eventDateStatus) queryParams.append('eventDateStatus', params.eventDateStatus);
    if (params.status) queryParams.append('status', params.status);
    if (params.dateType) queryParams.append('dateType', params.dateType);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.missingInfo) queryParams.append('isMissingInfo', 'true');
    
    // Always add pagination parameters
    queryParams.append('pageSize', params.pageSize.toString());
    queryParams.append('currentPage', params.currentPage.toString());

    const url = `${ROOT}/api/events/search?${queryParams}`;
    console.log('Fetching events from:', url);

    const response = await fetch(url);

    if (!response.ok) {
        const error = await response.json().catch(() => ({
            code: response.status,
            message: `Failed to fetch events (HTTP ${response.status})`,
        }));
        throw error;
    }

    return response.json();
};

export const DEFAULT_PAGE_SIZE = 20;
export const PAGINATION_SIZES = [5, 10, 25, 50, 100, 250];

/** Feedback produced by a toggleStatus call */
export interface ToggleFeedback {
    type: 'success' | 'error' | 'partial';
    text: string;
    /** Green-highlighted portion of the message (success count part) */
    successText?: string;
    /** Default-color remainder (already-in-state part) */
    remainderText?: string;
}

export interface SearchFiltersParams {
    searchText?: string;
    searchType?: string;
    searchField?: string;
    eventType?: string;
    eventGroup?: string;
    coverage?: string;
    eventDateStatus?: string;
    status?: string[];
    dateType?: string;
    startDate?: string;
    endDate?: string;
    missingInfo?: boolean;
}

export interface UseEventsState {
    events: EventData[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    loading: boolean;
    error: string | null;
}

export const useEvents = () => {
    // Initialize loading:true so the loading container is shown immediately on the
    // first render, preventing the brief "empty state → loading → data" layout shift.
    const [state, setState] = useState<UseEventsState>({
        events: [],
        totalCount: 0,
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        loading: true,
        error: null,
    });

    // Selection state
    const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);
    const [toggleStatusLoading, setToggleStatusLoading] = useState(false);
    const [toggleFeedback, setToggleFeedback] = useState<ToggleFeedback | null>(null);

    // Keep track of current search params for pagination
    const currentSearchParamsRef = useRef<EventSearchParams | null>(null);

    const performSearch = useCallback(async (searchParams: EventSearchParams) => {
        console.log('useEvents.performSearch called with:', searchParams);
        
        // Save current search parameters for pagination/page size changes
        currentSearchParamsRef.current = searchParams;

        // Clear any stale checkbox selections whenever new results are being fetched
        setSelectedEventIds([]);

        setState(prev => ({...prev, loading: true, error: null}));

        try {
            const response: EventSearchResponse = await searchEventsAsync(searchParams);
            console.log('API response:', response);

            setState(prev => ({
                ...prev,
                events: response.events,
                totalCount: response.totalRecords,
                currentPage: response.currentPage,
                pageSize: response.pageSize,
                loading: false,
                error: null,
            }));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch events';
            console.error('useEvents error:', errorMessage, err);
            setState(prev => ({
                ...prev,
                loading: false,
                error: errorMessage,
            }));
        }
    }, []);

    // Fetch default events (no filters)
    const fetchDefaultEvents = useCallback(() => {
        console.log('Fetching default events (no filters)');
        performSearch({status: 'Need to check', pageSize: DEFAULT_PAGE_SIZE, currentPage: 1});
    }, [performSearch]);

    // Search with filters
    const searchWithFilters = useCallback((filters: SearchFiltersParams) => {
        console.log('Searching with filters:', filters);
        
        // Check if all filters are empty
        const allEmpty = !filters.searchText && !filters.searchField && !filters.eventType && 
                         !filters.eventGroup && !filters.coverage && !filters.eventDateStatus && 
                         !filters.status?.length && !filters.dateType && !filters.startDate && !filters.endDate && !filters.missingInfo;
        
        if (allEmpty) {
            // If no filters, fetch default events
            fetchDefaultEvents();
        } else {
            // Build search params with ONLY selected filters (no defaults for unselected fields)
            const searchParams: EventSearchParams = {
                pageSize: DEFAULT_PAGE_SIZE,
                currentPage: 1,
                // Include all filters that user explicitly selected
                ...(filters.searchText && {searchText: filters.searchText}),
                ...(filters.searchType && {searchType: filters.searchType}),
                ...(filters.searchField && {searchField: filters.searchField}),
                ...(filters.eventType && {eventType: filters.eventType}),
                ...(filters.eventGroup && {eventGroup: filters.eventGroup}),
                ...(filters.coverage && {coverage: filters.coverage}),
                ...(filters.eventDateStatus && {eventDateStatus: filters.eventDateStatus}),
                ...(filters.status?.length && {status: filters.status.join(',')}),
                ...(filters.dateType && {dateType: filters.dateType}),
                ...(filters.startDate && {startDate: filters.startDate}),
                ...(filters.endDate && {endDate: filters.endDate}),
                ...(filters.missingInfo && {missingInfo: filters.missingInfo}),
            };
            performSearch(searchParams);
        }
    }, [fetchDefaultEvents, performSearch]);

    const doSearch = useCallback((params: Partial<EventSearchParams> = {}) => {
        setState(prev => {
            // Get existing search params or create new ones
            const baseParams = currentSearchParamsRef.current || {
                status: 'Need to check',
                pageSize: DEFAULT_PAGE_SIZE,
                currentPage: 1,
            };
            
            const searchParams: EventSearchParams = {
                ...baseParams,
                ...params,
                pageSize: params.pageSize ?? baseParams.pageSize ?? DEFAULT_PAGE_SIZE,
                currentPage: params.currentPage ?? baseParams.currentPage ?? 1,
            };
            
            // Fire async search
            performSearch(searchParams);
            
            return prev;
        });
    }, [performSearch]);

    const handlePageChange = useCallback(({currentPage, pageSize}: {currentPage: number, pageSize: number}) => {
        console.log('handlePageChange called:', {currentPage, pageSize});
        doSearch({currentPage, pageSize});
    }, [doSearch]);

    const handleGoToPage = useCallback((page: number) => {
        console.log('handleGoToPage called:', page);
        doSearch({currentPage: page});
    }, [doSearch]);

    const handlePageSizeChange = useCallback((size: number) => {
        console.log('handlePageSizeChange called:', size);
        doSearch({pageSize: size, currentPage: 1});
    }, [doSearch]);

    // ── Selection helpers ────────────────────────────────────────────────────

    const toggleSelectEvent = useCallback((eventId: number) => {
        setSelectedEventIds(prev =>
            prev.includes(eventId)
                ? prev.filter(id => id !== eventId)
                : [...prev, eventId]
        );
    }, []);

    /** Select all if not all are selected; deselect all otherwise. */
    const toggleSelectAll = useCallback((allEventIds: number[]) => {
        setSelectedEventIds(prev =>
            allEventIds.every(id => prev.includes(id)) ? [] : [...allEventIds]
        );
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedEventIds([]);
    }, []);

    const clearToggleFeedback = useCallback(() => {
        setToggleFeedback(null);
    }, []);

    // ── Toggle status ────────────────────────────────────────────────────────

    /**
     * Toggle status for the provided event IDs.
     * targetStatus is case-insensitive; normalised to uppercase before the API call.
     *
     * Guarantees: successCount + failureCount === eventIds.length in all feedback messages.
     * The grid is refreshed in the background (fire-and-forget) so feedback appears instantly.
     */
    const toggleStatus = useCallback(async (targetStatus: string, eventIds: number[]) => {
        // ── 1. Log immediately so Network tab / console shows the intent ──────
        console.log('[toggleStatus] targetStatus:', targetStatus.toUpperCase(), '| selectedIds:', eventIds);

        // ── 2. Guard: nothing selected ────────────────────────────────────────
        if (eventIds.length === 0) {
            setToggleFeedback({type: 'error', text: 'Please select at least one event'});
            return;
        }

        setToggleStatusLoading(true);
        setToggleFeedback(null);

        try {
            const response = await toggleEventStatus(eventIds, targetStatus);
            const objectData = Array.isArray(response.objectData) ? response.objectData : [];

            // Deduplicate by eventId so we never exceed selectedIds.length.
            const selectedSet = new Set(eventIds);
            const dedupMap    = new Map<number, typeof objectData[0]>();
            for (const item of objectData) {
                if (selectedSet.has(item.eventId)) {
                    dedupMap.set(item.eventId, item);
                }
            }
            const relevantData = Array.from(dedupMap.values());
            const successList  = relevantData.filter(r => r.success);

            // ── 3. Guarantee successCount + failureCount === eventIds.length ──
            // Any IDs the API omitted are treated as "already in that state".
            const successCount = successList.length;
            const failureCount = eventIds.length - successCount; // always non-negative

            const normalAction = targetStatus.toUpperCase() as ToggleAction;
            const actionLabel  = normalAction === 'ACTIVE' ? 'Active' : 'Inactive';

            if (failureCount === 0) {
                // All events changed successfully
                const text = `Status changed to ${actionLabel} for ${successCount} event(s)`;
                setToggleFeedback({type: 'success', text, successText: text});
            } else if (successCount === 0) {
                // No events changed (all already in target state)
                setToggleFeedback({
                    type: 'error',
                    text: `All selected events are already ${actionLabel}`,
                });
            } else {
                // Mixed: some changed, some already in target state
                const successPart   = `Status changed to ${actionLabel} for ${successCount} event(s)`;
                const remainderPart = `, ${failureCount} event(s) were already ${actionLabel}`;
                setToggleFeedback({
                    type: 'partial',
                    text: `${successPart}${remainderPart}`,
                    successText: successPart,
                    remainderText: remainderPart,
                });
            }

            // Release loader immediately so feedback is shown without waiting for the grid
            setToggleStatusLoading(false);

            // Refresh grid in the background (fire-and-forget)
            if (successCount > 0 && currentSearchParamsRef.current) {
                performSearch(currentSearchParamsRef.current);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to toggle event status';
            setToggleFeedback({type: 'error', text: errorMessage});
            setToggleStatusLoading(false);
        }
    }, [performSearch]);

    return {
        ...state,
        selectedEventIds,
        toggleStatusLoading,
        toggleFeedback,
        doSearch,
        fetchDefaultEvents,
        searchWithFilters,
        handlePageChange,
        handleGoToPage,
        handlePageSizeChange,
        toggleSelectEvent,
        toggleSelectAll,
        clearSelection,
        clearToggleFeedback,
        toggleStatus,
    };
};
